import { z } from 'zod';
import { toScaled } from './money.ts';

/**
 * Conciliação bancária (ADR 0084, Entrega 9): sugestão de casamento entre as linhas do extrato
 * e as baixas registradas (pagamentos do Contas a Pagar e transferências). Só sugere: quem
 * confirma é uma pessoa. Quando há mais de um candidato igualmente bom, a sugestão é "ambígua" e
 * nenhum é escolhido.
 */

export const RECONCILIATION_BOOK_TYPES = [
  'payables.movement',
  'treasury.transfer.out',
  'treasury.transfer.in',
  'treasury.receipt',
] as const;
export type ReconciliationBookType = (typeof RECONCILIATION_BOOK_TYPES)[number];

export const RECONCILIATION_DIFFERENCE_KINDS = [
  'fee',
  'interest',
  'fine',
  'discount',
  'fx',
  'other',
] as const;

/** Janela padrão entre a data do extrato e a da baixa (D-09: o banco pode liquidar depois). */
export const RECONCILIATION_WINDOW_DAYS = 3;

export interface ReconBankLine {
  id: string;
  date: string;
  /** Com sinal: entrada positiva, saída negativa. */
  amount: string;
  reference: string | null;
}

export interface ReconBookItem {
  type: ReconciliationBookType;
  id: string;
  date: string;
  amount: string;
  reference: string | null;
}

export type ReconSuggestion =
  | { bankId: string; status: 'suggested'; book: { type: ReconciliationBookType; id: string } }
  | {
      bankId: string;
      status: 'ambiguous';
      candidates: { type: ReconciliationBookType; id: string }[];
    }
  | { bankId: string; status: 'none' };

const DAY_MS = 86_400_000;
const daysBetween = (a: string, b: string) =>
  Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / DAY_MS;
const norm = (v: string | null) => (v ?? '').trim().toUpperCase();

/**
 * Para cada linha do extrato: candidatos com o mesmo valor (com sinal) e data dentro da janela.
 * Nota: dia de diferença tira pontos; referência igual soma. Empate no melhor, ou a mesma baixa
 * sendo a melhor de duas linhas, vira "ambígua".
 */
export function suggestMatches(
  bank: readonly ReconBankLine[],
  book: readonly ReconBookItem[],
  windowDays = RECONCILIATION_WINDOW_DAYS,
): ReconSuggestion[] {
  const best = new Map<string, { score: number; items: ReconBookItem[] }>();
  for (const line of bank) {
    const amount = toScaled(line.amount);
    let top = -Infinity;
    let items: ReconBookItem[] = [];
    for (const it of book) {
      if (toScaled(it.amount) !== amount) continue;
      const days = daysBetween(line.date, it.date);
      if (days > windowDays) continue;
      const score =
        100 -
        days * 10 +
        (norm(line.reference) && norm(line.reference) === norm(it.reference) ? 5 : 0);
      if (score > top) {
        top = score;
        items = [it];
      } else if (score === top) {
        items.push(it);
      }
    }
    best.set(line.id, { score: top, items });
  }
  // A mesma baixa como melhor candidata de mais de uma linha: nenhuma das linhas a recebe.
  const claims = new Map<string, number>();
  for (const { items } of best.values()) {
    if (items.length === 1) {
      const k = `${items[0]!.type}|${items[0]!.id}`;
      claims.set(k, (claims.get(k) ?? 0) + 1);
    }
  }
  return bank.map((line) => {
    const { items } = best.get(line.id)!;
    if (items.length === 0) return { bankId: line.id, status: 'none' };
    const only = items.length === 1 ? items[0]! : null;
    if (only && claims.get(`${only.type}|${only.id}`) === 1) {
      return { bankId: line.id, status: 'suggested', book: { type: only.type, id: only.id } };
    }
    return {
      bankId: line.id,
      status: 'ambiguous',
      candidates: items.map((it) => ({ type: it.type, id: it.id })),
    };
  });
}

export const reconcileInput = z.object({
  accountKey: z.uuid(),
  bankMovementIds: z.array(z.uuid()).min(1).max(50),
  bookItems: z
    .array(z.object({ type: z.enum(RECONCILIATION_BOOK_TYPES), id: z.uuid() }))
    .max(50)
    .default([]),
  differenceKind: z.enum(RECONCILIATION_DIFFERENCE_KINDS).nullable().optional(),
  reason: z
    .string()
    .trim()
    .max(1000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  method: z.enum(['suggested', 'manual']).default('manual'),
});
export type ReconcileInput = z.infer<typeof reconcileInput>;

export const undoReconciliationInput = z.object({ reason: z.string().trim().min(3).max(1000) });
